import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone } from 'lucide-react';
import Header from '../../components/marketing/Header';
import Footer from '../../components/marketing/Footer';
import Seo, { faqSchema } from '../../components/Seo';
import SubscriptionInterestForm from '../../components/SubscriptionInterestForm';
import TalkToAlfred from '../../components/TalkToAlfred';
import ChatWithAlfred from '../../components/ChatWithAlfred';

/**
 * Reusable Industry Landing Page — Alfred Voice, the Voice Executive Assistant.
 *
 * Brand: the "calm, capable concierge" system (AlfredVoice_Style_Guide.md).
 * White page, warm cards, deep ink, muted brass as a seasoning, Poppins only,
 * pill buttons, and the soundwave as the single flourish. One dark card per
 * screenful carries the statement you most want remembered (here: the ROI strip).
 *
 * Usage: <IndustryLandingPage config={hvacConfig} />
 */

// ---- Line drawings (inherit color via currentColor) ------------------------
const strokeProps = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2.25,
  strokeLinecap: 'round',
  strokeLinejoin: 'round'
};

const DrawCall = (props) => (
  <svg viewBox="0 0 96 72" className="w-16 h-16" {...strokeProps} {...props}>
    <path d="M16 14h52a8 8 0 0 1 8 8v22a8 8 0 0 1-8 8H40l-14 12V52H16a8 8 0 0 1-8-8V22a8 8 0 0 1 8-8Z" />
    <path d="M26 28h28M26 38h18" />
  </svg>
);
const DrawSale = (props) => (
  <svg viewBox="0 0 96 72" className="w-16 h-16" {...strokeProps} {...props}>
    <path d="M14 10v50h68" />
    <path d="M24 50l16-16 12 10 22-26" />
    <path d="M66 18h12v12" />
  </svg>
);
const DrawTeam = (props) => (
  <svg viewBox="0 0 96 72" className="w-16 h-16" {...strokeProps} {...props}>
    <circle cx="34" cy="26" r="11" />
    <path d="M14 60c0-12 9-19 20-19s20 7 20 19" />
    <circle cx="64" cy="30" r="8" />
    <path d="M58 42c10-2 22 4 22 18" />
  </svg>
);
const PILLAR_DRAWINGS = [DrawCall, DrawSale, DrawTeam];
const PILLAR_BENEFITS = ['Saves You Time', 'Increases Revenue', 'Reduces Expenses'];

const Wave = ({ live = false, className = '' }) => (
  <span className={`av-wave ${live ? 'av-wave--live' : ''} ${className}`} aria-hidden="true">
    <span /><span /><span /><span /><span /><span /><span />
  </span>
);

// Simple bar chart for the ROI strip (renders on the dark card)
const BarChart = () => (
  <svg viewBox="0 0 220 120" className="w-full max-w-md mx-auto" {...strokeProps}>
    <path d="M24 8v96h180" />
    <rect x="48" y="74" width="26" height="30" />
    <rect x="96" y="50" width="26" height="54" />
    <rect x="144" y="24" width="26" height="80" />
    <path d="M44 96l52-30 52-34" strokeDasharray="4 5" />
  </svg>
);

// `canonicalPath` comes from the route in App.jsx rather than from the config,
// because several of these pages are reachable at TWO urls (/hvac is an alias of
// /industries/hvac). Both routes pass the /industries/* path, so the alias points
// its canonical at the real page instead of competing with it for the same terms.
const IndustryLandingPage = ({ config, canonicalPath }) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const handlePlanSelect = (planId) => {
    setSelectedPlan(planId);
    setIsFormOpen(true);
  };

  const {
    industry,
    heroTitle,
    heroSubtitle,
    heroTag,
    heroImage,
    stats,
    showTalkToAlfred,
    talkToAlfredTitle,
    talkToAlfredSubtitle,
    pillars,
    pillarsTitle,
    pillarsSubtitle,
    painPoints,
    comparison,
    integrations,
    features,
    featuresTitle,
    featuresSubtitle,
    howItWorks,
    pricingPlans,
    roiCalculator,
    faq,
    ctaHeading,
    demoPhone
  } = config;

  const CALL = demoPhone || '(346) 258-0006';
  const TEL = `tel:${(demoPhone || '3462580006').replace(/[^\d+]/g, '')}`;

  useEffect(() => {
    window.scrollTo(0, 0);

    const handleAnchorClick = (e) => {
      const href = e.target.closest('a')?.getAttribute('href');
      if (href?.startsWith('#')) {
        e.preventDefault();
        const target = document.querySelector(href);
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };
    document.addEventListener('click', handleAnchorClick);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateY(0)';
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    setTimeout(() => {
      document.querySelectorAll('.fade-in').forEach((card) => {
        card.style.opacity = '0';
        card.style.transform = 'translateY(30px)';
        card.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
        observer.observe(card);
      });
    }, 100);

    return () => {
      document.removeEventListener('click', handleAnchorClick);
      observer.disconnect();
    };
  }, []);

  // Meta derived from the config the page already renders, so a copy change moves
  // the search result with it. A config may override with `seoTitle`/`seoDescription`
  // when the hero wording is too short or too clever to stand alone in a SERP.
  const seoTitle = config.seoTitle || `${heroTitle} | Alfred Voice for ${industry}`;
  const seoDescription = (config.seoDescription || heroSubtitle || '').slice(0, 300);

  return (
    <div className="min-h-screen bg-paper text-ink">
      {/* Rendered only when the route supplied a canonical path — without one we
          cannot state which URL is authoritative, and a wrong canonical is worse
          than none (it de-indexes the real page). */}
      {canonicalPath && (
        <Seo
          title={seoTitle}
          description={seoDescription}
          path={canonicalPath}
          structuredData={
            faq?.length
              ? [faqSchema(faq.map((f) => ({ question: f.q, answer: f.a })))]
              : []
          }
        />
      )}
      <Header callNumber={CALL} tel={TEL} />

      {/* Hero — white page, warm accents, brass seasoning */}
      <section className="relative overflow-hidden border-b border-hair">
        <div className="pointer-events-none absolute inset-0" style={{
          background: 'radial-gradient(700px 380px at 82% 10%, rgba(168,127,69,.10), transparent 60%), radial-gradient(620px 460px at 10% 95%, rgba(14,27,38,.05), transparent 60%)'
        }} />
        <div className="relative max-w-4xl mx-auto px-6 py-24 text-center">
          <div className="flex justify-center mb-6"><Wave live /></div>
          {heroTag && <span className="av-eyebrow mb-4">{heroTag}</span>}
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-[1.05] tracking-tight">
            {heroTitle}
          </h1>
          <p className="text-lg md:text-xl mb-10 text-slateink font-light max-w-2xl mx-auto">
            {heroSubtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href={TEL} className="av-btn-brass px-8 py-4 text-sm">
              <Phone className="w-4 h-4" /> Talk to Alfred — {CALL}
            </a>
            <button type="button" data-chat-open className="av-btn-ghost px-8 py-4 text-sm">
              Chat with Alfred
            </button>
          </div>
          {showTalkToAlfred && (
            <div className="mt-4 text-sm text-slateink flex items-center justify-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-brass" /> Live in days · works with your existing number
            </div>
          )}

          {heroImage && (
            <div className="flex justify-center mt-14">
              <img
                src={heroImage}
                alt={`Alfred — Voice Executive Assistant for ${industry}`}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                className="rounded-brand w-full max-w-2xl object-cover border border-hair shadow-[0_24px_60px_-28px_rgba(14,27,38,.35)]"
              />
            </div>
          )}

          {stats?.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16 max-w-3xl mx-auto border-t border-hair pt-12">
              {stats.map((stat, idx) => (
                <div key={idx} className="text-center">
                  <div className="text-4xl font-bold mb-1 text-brass">{stat.value}</div>
                  <div className="text-sm text-slateink">{stat.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Hear Alfred live */}
      {showTalkToAlfred && (
        <section id="talk-to-alfred" className="py-20 bg-paper border-b border-hair">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="av-eyebrow mb-3">Hear it for yourself</span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {talkToAlfredTitle || "Don't take our word for it. Hear Alfred."}
              </h2>
              <p className="text-lg text-slateink max-w-2xl mx-auto">
                {talkToAlfredSubtitle || 'Pick a role, enter your number, and Alfred calls you in about a minute.'}
              </p>
            </div>
            <TalkToAlfred showHeading={false} />
          </div>
        </section>
      )}

      {/* Three Pillars */}
      {pillars?.length > 0 && (
        <section className="py-20 bg-warm border-b border-hair">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-16">
              <span className="av-eyebrow mb-3">Cut expenses · Save time · Grow revenue</span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {pillarsTitle || 'Three jobs. One Voice Executive Assistant.'}
              </h2>
              <p className="text-lg text-slateink max-w-2xl mx-auto">
                {pillarsSubtitle || 'Reduce expenses. Save time. Increase revenue.'}
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-12">
              {pillars.map((pillar, idx) => {
                const Drawing = PILLAR_DRAWINGS[idx] || DrawCall;
                return (
                  <div key={pillar.title} className="fade-in text-center md:text-left">
                    <div className="text-brass mb-5 flex justify-center md:justify-start">
                      <Drawing />
                    </div>
                    <h3 className="text-2xl md:text-3xl font-bold mb-2">
                      {pillar.benefit || PILLAR_BENEFITS[idx]}
                    </h3>
                    <div className="av-eyebrow mb-3" style={{ color: '#5A6672' }}>
                      {pillar.title}
                    </div>
                    <p className="text-slateink leading-relaxed">{pillar.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Pain Points */}
      {painPoints?.length > 0 && (
        <section className="py-20 bg-paper">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-14">
              <span className="av-eyebrow mb-3">Sound familiar?</span>
              <h2 className="text-3xl md:text-4xl font-bold">The calls you're losing right now.</h2>
            </div>
            <div className="space-y-4">
              {painPoints.map((point, idx) => (
                <div key={idx} className="fade-in bg-warm border border-hair rounded-brand p-6 md:flex md:items-center md:gap-6">
                  <div className="flex items-start gap-3 md:w-1/2 mb-4 md:mb-0">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full border-2 border-slateink text-slateink flex items-center justify-center font-bold text-sm">✕</span>
                    <p className="font-semibold">{point.problem}</p>
                  </div>
                  <div className="flex items-start gap-3 md:w-1/2 md:border-l md:border-hair md:pl-6">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-brass text-white flex items-center justify-center font-bold text-sm">✓</span>
                    <p className="text-slateink">{point.solution}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Features */}
      {features?.length > 0 && (
        <section id="features" className="py-20 bg-warm border-y border-hair">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <span className="av-eyebrow mb-3">A Voice Executive Assistant</span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {featuresTitle || `Built for ${industry}`}
              </h2>
              <p className="text-lg text-slateink">
                {featuresSubtitle || 'A Voice Executive Assistant, not just an answering service.'}
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map(({ title, description, Icon, benefit }) => (
                <div key={title} className="fade-in bg-paper border border-hair p-8 rounded-brand hover:shadow-lg transition-shadow">
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 text-brass" style={{ background: 'rgba(168,127,69,.12)', border: '1px solid #A87F45' }}>
                    {Icon && <Icon className="w-7 h-7" />}
                  </div>
                  {benefit && (
                    <div className="av-eyebrow mb-2" style={{ color: '#5A6672' }}>{benefit}</div>
                  )}
                  <h3 className="text-xl font-bold mb-3">{title}</h3>
                  <p className="text-slateink leading-relaxed">{description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Comparison */}
      {comparison?.rows?.length > 0 && (
        <section className="py-20 bg-paper border-b border-hair">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              {comparison.title || 'Why Alfred is different'}
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full border border-hair text-left rounded-brand overflow-hidden">
                <thead>
                  <tr>
                    <th className="p-4 bg-warm border-b border-hair" />
                    {comparison.columns.map((col, i) => (
                      <th key={col} className={`p-4 border-b border-hair text-center font-bold ${i === 0 ? 'bg-ink text-white' : 'bg-warm text-slateink'}`}>
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {comparison.rows.map((row) => (
                    <tr key={row.label} className="border-b border-hair">
                      <td className="p-4 font-semibold">{row.label}</td>
                      {row.values.map((v, i) => (
                        <td key={i} className={`p-4 text-center text-lg ${i === 0 ? 'bg-warm' : ''}`}>
                          {v ? <span className="text-brass font-bold">✓</span> : <span className="text-hair">—</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* How It Works */}
      {howItWorks?.length > 0 && (
        <section className="py-20 bg-warm border-b border-hair">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">How it works</h2>
            <div className="grid md:grid-cols-3 gap-10">
              {howItWorks.map((step, idx) => (
                <div key={idx} className="fade-in text-center">
                  <div className="w-14 h-14 bg-brass text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-6">
                    {idx + 1}
                  </div>
                  <h3 className="text-xl font-bold mb-3">{step.title}</h3>
                  <p className="text-slateink">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Integrations */}
      {integrations?.length > 0 && (
        <section className="py-20 bg-paper border-b border-hair">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="av-eyebrow mb-3">No rip-and-replace</span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Works with your tools</h2>
              <p className="text-lg text-slateink">Alfred fits the systems you already use.</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {integrations.map((integration) => (
                <div key={integration.name} className="fade-in bg-warm border border-hair p-6 rounded-brand text-center hover:shadow-lg transition-shadow">
                  <h3 className="text-lg font-bold mb-2">{integration.name}</h3>
                  <p className="text-slateink text-sm">{integration.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Pricing */}
      {pricingPlans?.length > 0 && (
        <section id="pricing" className="py-20 bg-warm border-b border-hair">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <span className="av-eyebrow mb-3">Simple, flat monthly plans</span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Pricing</h2>
              <p className="text-lg text-slateink">Month-to-month. Cancel anytime. No long-term contracts.</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {pricingPlans.map((plan) => {
                const cardClasses = plan.featured
                  ? 'fade-in bg-paper p-8 rounded-brand border-2 border-brass shadow-xl'
                  : 'fade-in bg-paper p-8 rounded-brand border border-hair shadow-md';
                return (
                  <div key={plan.name} className={cardClasses}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="av-eyebrow" style={{ color: '#5A6672' }}>{plan.name}</div>
                      {plan.featured && (
                        <span className="text-xs font-semibold text-white bg-brass px-3 py-1 rounded-full">Most Popular</span>
                      )}
                    </div>
                    <div className="text-4xl font-bold mb-1 text-brass">{plan.price}</div>
                    {plan.setup && <div className="text-sm text-slateink mb-3">{plan.setup}</div>}
                    <p className="text-slateink mb-6 leading-relaxed">{plan.headline}</p>
                    <ul className="space-y-3 text-ink mb-8">
                      {plan.callouts.map((item) => (
                        <li key={item} className="flex items-start gap-3">
                          <span className="text-brass font-bold mt-0.5">✓</span>
                          <span className="text-sm text-slateink">{item}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => handlePlanSelect(plan.name.toLowerCase().split(' ')[0])}
                      className={`w-full px-6 py-3 text-sm ${plan.featured ? 'av-btn-brass' : 'av-btn-ink'}`}
                    >
                      {plan.cta}
                    </button>
                  </div>
                );
              })}
            </div>
            <p className="mt-10 text-center text-sm text-slateink">
              Free 3-day trial · Spanish included free · premium voices &amp; other languages available · overage $0.35/min.
            </p>
          </div>
        </section>
      )}

      {/* ROI — the ONE dark card per screen */}
      {roiCalculator && (
        <section className="py-20 bg-paper">
          <div className="max-w-5xl mx-auto px-6">
            <div className="bg-ink text-white rounded-brand p-10 md:p-14">
              <div className="text-center mb-12">
                <span className="av-eyebrow mb-3">The math</span>
                <h2 className="text-3xl md:text-4xl font-bold text-white">{roiCalculator.title}</h2>
              </div>
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div className="text-brass"><BarChart /></div>
                <div>
                  <div className="grid grid-cols-3 gap-4 mb-8">
                    {roiCalculator.metrics.map((metric, idx) => (
                      <div key={idx}>
                        <div className="text-3xl font-bold mb-1 text-brass">{metric.value}</div>
                        <p className="text-xs text-mist">{metric.label}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-lg text-mist mb-8">{roiCalculator.conclusion}</p>
                  <a href={TEL} className="av-btn-brass px-8 py-4 text-sm">
                    <Phone className="w-4 h-4" /> {roiCalculator.cta || `Talk to Alfred — ${CALL}`}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {faq?.length > 0 && (
        <section className="py-20 bg-warm border-y border-hair">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Questions, answered</h2>
            <div className="divide-y divide-hair border-t border-b border-hair">
              {faq.map((item) => (
                <details key={item.q} className="group py-5">
                  <summary className="flex cursor-pointer items-center justify-between font-semibold text-lg list-none">
                    {item.q}
                    <span className="ml-4 text-2xl leading-none text-brass group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="mt-3 text-slateink leading-relaxed">{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-paper py-20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <span className="av-eyebrow mb-3">See it on your own line</span>
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            {ctaHeading || `Ready to transform your ${industry} business?`}
          </h2>
          <p className="text-lg mb-10 text-slateink">
            Tell us how your calls work today. We configure, tune, and launch Alfred for you—live in days.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href={TEL} className="av-btn-brass px-8 py-4 text-sm">
              <Phone className="w-4 h-4" /> Talk to Alfred — {CALL}
            </a>
            <button type="button" data-chat-open className="av-btn-ghost px-8 py-4 text-sm">
              Chat with Alfred
            </button>
          </div>
        </div>
      </section>

      <Footer />

      <SubscriptionInterestForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        preselectedPlan={selectedPlan}
      />

      <ChatWithAlfred source={`landing-${industry || 'general'}`} callNumber={CALL} />
    </div>
  );
};

export default IndustryLandingPage;
